import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { getDateString, updateURLWithParams } from '../../../services/utils.ts';
import { useAlert } from '../../../contexts/alert-context.tsx';
import { URLS } from '../../../services/urls.ts';
import { IJob, fetchJobs } from '../../../services/stixify_api.ts';
import { TeamRouteContext } from '../../team-layout.tsx/index.tsx';

const PAGE_SIZE = 10;


const Jobs: React.FC = () => {
    const [jobs, setJobs] = useState<IJob[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(1);
    const { teamId } = useContext(TeamRouteContext)

    // // Filter and sorting states
    const [filter, setFilter] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [sortField, setSortField] = useState<string>('run_datetime');
    const [initialDataLoaded, setInitialDataLoaded] = useState(false)
    const location = useLocation();
    const alert = useAlert()

    useEffect(() => {
        updateURLWithParams({
            sortOrder, page, sortField
        })
    }, [sortOrder, page, sortField])

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        setSortOrder(query.get('sortOrder') === 'asc' ? 'asc' : 'desc')
        setPage(Number(query.get('page') || 0))
        setFilter(query.get('filter') || '')
        setSortField(query.get('sortField') || 'run_datetime')
        setInitialDataLoaded(true)
    }, [location])

    useEffect(() => { loadJobs(page) }, [initialDataLoaded])

    useEffect(() => {
        loadJobs(page);
    }, [teamId, page, filter, sortField, sortOrder]);

    const loadJobs = async (pageNumber: number) => {
        if (!initialDataLoaded) return
        setLoading(true);
        const response = await fetchJobs(teamId, pageNumber + 1, sortField, sortOrder);
        setJobs(response.data.results);
        setTotalPages(Math.ceil(response.data.count / PAGE_SIZE));
        setLoading(false);
    };

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleSortChange = (field: string) => {
        const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortOrder(newOrder);
        setPage(0); // Reset to first page on sort change
    };

    useEffect(() => {
        document.title = 'Upload Status | Stixify Web'
    }, [])

    const getReportUrl = (job: IJob) => {
      if(!teamId) return URLS.staffReportDetail(job.report)
      return URLS.teamReportDetail(teamId, job.report)
    }

    const getJobDetailUrl = (job: IJob) => {
      if(!teamId) return URLS.staffJobDetail(job.report, job.id)
      return URLS.teamJobDetail(teamId, job.report, job.id)
    }

    return (
        <Box p={4}>
            <Typography variant="h4" gutterBottom>
                Upload Status
            </Typography>
            <Typography>
                <p>Every Report uploaded is tied to a Job. The table below shows all Jobs belonging to Reports. Use this page to track the Jobs for Reports uploaded by your team.</p>
            </Typography>
            {(loading) ? (
                <CircularProgress style={{ display: 'block', margin: '20px auto' }} />
            ) : (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell onClick={() => handleSortChange('id')} style={{ cursor: 'pointer' }}>
                                    Job ID  {sortField === 'id' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </TableCell>
                                <TableCell onClick={() => handleSortChange('id')} style={{ cursor: 'pointer' }}>
                                    Report ID
                                </TableCell>
                                <TableCell onClick={() => handleSortChange('run_datetime')} style={{ cursor: 'pointer' }}>
                                    Run Datetime {sortField === 'run_datetime' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </TableCell>
                                <TableCell onClick={() => handleSortChange('state')} style={{ cursor: 'pointer' }}>
                                    State {sortField === 'state' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </TableCell>
                                <TableCell>Completion</TableCell>
                                <TableCell>Error</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {jobs.map((job) => (
                                <TableRow key={job.id}>
                                    <TableCell>
                                        <Link to={getJobDetailUrl(job)}>
                                            {job.id}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <Link to={getReportUrl(job)}>
                                            {job.report}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{getDateString(job.run_datetime)}</TableCell>
                                    <TableCell>{job.state}</TableCell>
                                    <TableCell>{getDateString(job.metadata?.completion_time)}</TableCell>
                                    <TableCell>{job.metadata?.error}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )
            }

            <TablePagination
                rowsPerPageOptions={[]}
                component="div"
                count={totalPages * PAGE_SIZE}
                rowsPerPage={PAGE_SIZE}
                page={page}
                onPageChange={handlePageChange}
                labelRowsPerPage={""}
                style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}
            />
        </Box >
    );
};

export default Jobs;